import type { ReactNode } from "react";
import { cn } from "./cn";

type Tone = "success" | "danger" | "warning" | "brand" | "neutral";

const tones: Record<Tone, string> = {
  success: "bg-success-soft text-success-ink",
  danger: "bg-danger-soft text-danger-ink",
  warning: "bg-warning-soft text-warning-ink",
  brand: "bg-brand-soft text-brand-ink",
  neutral: "bg-surface-sunken text-ink-3",
};

const dots: Record<Tone, string> = {
  success: "bg-success",
  danger: "bg-danger",
  warning: "bg-warning",
  brand: "bg-brand",
  neutral: "bg-ink-4",
};

export function StatusPill({
  tone = "neutral",
  dot = false,
  pulse = false,
  className,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  pulse?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-2xs font-semibold whitespace-nowrap",
        tones[tone],
        className,
      )}
    >
      {dot ? (
        <span className="relative flex size-1.5">
          {pulse ? (
            <span
              className={cn(
                "absolute inline-flex size-full animate-ping rounded-full opacity-70",
                dots[tone],
              )}
            />
          ) : null}
          <span className={cn("relative inline-flex size-1.5 rounded-full", dots[tone])} />
        </span>
      ) : null}
      {children}
    </span>
  );
}

/** Maps the domain's outcome vocabulary onto pill tones in one place. */
export function outcomeTone(outcome: string): Tone {
  if (["success", "succeeded", "active", "approved", "verified"].includes(outcome))
    return "success";
  if (["denied", "failed", "rejected", "suspended"].includes(outcome)) return "danger";
  if (["pending", "in review"].includes(outcome)) return "warning";
  return "neutral";
}
